import { TypedFormik } from '@dai/components'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Alert,
  Button,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import LanguageSelector from '@/components/common/LanguageSelector'
import useAuth from '@/hooks/useAuth'
import useT from '@/i18n/useT'

function SignIn() {
  const location = useLocation()
  const from = location.state?.from
  const navigate = useNavigate()
  const { signIn } = useAuth()
  const { t } = useT()
  const [showPassword, setShowPassword] = useState(false)

  const handleClickShowPassword = () => setShowPassword(show => !show)

  const ERROR_MESSAGES: Record<string, string> = {
    'auth/user-not-found': t('login.auth.userNotFound'),
    'auth/wrong-password': t('login.auth.wrongPassword'),
    'auth/too-many-requests': t('login.auth.tooManyRequests'),
    'auth/invalid-credential': t('login.auth.invalidCredential'),
  }

  const getErrorMessageFromError = (error: any) => {
    const keyToCheck = Object.keys(ERROR_MESSAGES).find(
      key => error.message && error.message.includes(key),
    )
    if (keyToCheck) {
      return ERROR_MESSAGES[keyToCheck]
    }
    return error.message
  }

  return (
    <Stack spacing={2}>
      <TypedFormik
        initialValues={{
          email: '',
          password: '',
        }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email(t('login.validation.invalidEmail'))
            .max(255)
            .required(t('login.validation.emailOrUsernameRequired')),
          password: Yup.string()
            .max(255)
            .required(t('login.validation.passwordRequired')),
        })}
        onSubmit={async (values, { setStatus }) => {
          try {
            await signIn(values.email, values.password)
            setStatus({ success: true })
            if (from) {
              navigate(from)
            }
          } catch (error: any) {
            const message = getErrorMessageFromError(error)
            setStatus({ success: false, message })
          }
        }}
      >
        {({
          status,
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            <Stack spacing={4}>
              {status?.message && (
                <Alert severity={status.success ? 'success' : 'warning'}>
                  {status.message}
                </Alert>
              )}
              <TextField
                type="email"
                name="email"
                label={t('login.emailAddressOrUsername')}
                value={values.email}
                error={Boolean(touched.email && errors.email)}
                fullWidth
                helperText={touched.email && errors.email}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              <Stack spacing={2}>
                <TextField
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  label={t('login.password')}
                  value={values.password}
                  error={Boolean(touched.password && errors.password)}
                  fullWidth
                  helperText={touched.password && errors.password}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleClickShowPassword}
                            edge="end"
                            aria-label={t(
                              'common.password.toggleVisibility.aria',
                            )}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Typography component="div" variant="caption">
                  <Link
                    to="/auth/action?mode=resetPassword"
                    component={RouterLink}
                  >
                    {t('login.forgotPassword.message')}
                  </Link>
                </Typography>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  loading={isSubmitting}
                  loadingPosition="end"
                >
                  {t('login.signIn')}
                </Button>
              </Stack>

              <Typography sx={{ textAlign: 'center' }}>
                {t('login.noAccountYet.message')}{' '}
                <Link to="/auth/sign-up" component={RouterLink}>
                  {t('login.signUp')}
                </Link>
              </Typography>
            </Stack>
          </form>
        )}
      </TypedFormik>
      <LanguageSelector direction="row-reverse" />
    </Stack>
  )
}

export default SignIn
